import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ServiceIcon } from '../shared/page-header/page-header.component';
import { CustomModel } from '../../models/custom-model.model';
import { FireflyGenerationOptions, FireflyGenerationOptionsIm5 } from '../../models/firefly-common.model';
import { CustomModelService } from '../../services/custom-model.service';
import { FireflyService } from '../../services/firefly.service';
import { FireflyConfigService } from '../../services/firefly-config.service';
import { ToastService } from '../../services/toast.service';
import { LogService } from '../../services/log.service';
import { I18nService } from '../../i18n/i18n.service';

interface GeneratedImage {
  url: string;
  seed?: number;
}

interface GenerationSettings {
  numImages: number;
  size: string;
}

@Component({
  selector: 'app-custom-model-generator',
  templateUrl: './custom-model-generator.component.html',
  styleUrls: ['./custom-model-generator.component.scss'],
  standalone: false
})
export class CustomModelGeneratorComponent implements OnInit, OnDestroy {
  headerIcons: ServiceIcon[] = [
    { src: '/assets/icon_firefly.svg', alt: 'Firefly' }
  ];

  customModels: CustomModel[] = [];
  selectedModel: CustomModel | null = null;
  selectedModelId: string | null = null;
  selectedModelVersion: string = '';
  isLoadingCustomModels = false;

  prompt: string = '';
  structureImageId: string | null = null;
  structureImagePreview: string | null = null;
  isUploadingStructure = false;
  isDraggingStructure = false;
  isGenerating: boolean = false;

  generatedImages: GeneratedImage[] = [];
  generationPrompt: string = '';
  hasGeneratedImages = false;

  // Pixel sizes are only used by IM4
  readonly pixelSizeOptions = [
    { label: 'Square (1:1)', value: '2048x2048' },
    { label: 'Portrait (4:3)', value: '2304x1792' },
    { label: 'Landscape (3:4)', value: '1792x2304' },
    { label: 'Wide (16:9)', value: '2688x1536' },
    { label: 'Portrait (9:16)', value: '1440x2560' },
  ];

  // Aspect ratios are only used by IM5
  readonly aspectRatioOptions = [
    { label: 'Square (1:1)', value: '1:1' },
    { label: 'Landscape (4:3)', value: '4:3' },
    { label: 'Portrait (3:4)', value: '3:4' },
    { label: 'Wide (16:9)', value: '16:9' },
    { label: 'Portrait (9:16)', value: '9:16' },
  ];

  get usesAspectRatioApi(): boolean {
    return this.selectedModelVersion === 'image5_custom';
  }

  get sizeOptions() {
    return this.usesAspectRatioApi ? this.aspectRatioOptions : this.pixelSizeOptions;
  }

  get hasStructureImage(): boolean {
    return !!this.structureImagePreview || !!this.structureImageId || this.isUploadingStructure;
  }

  generationSettings: GenerationSettings = {
    numImages: 2,
    size: '2048x2048',
  };

  // Maximum file size upload
  private readonly structureImageMaxBytes = 15 * 1024 * 1024;

  private destroy$ = new Subject<void>();

  constructor(
    private customModelService: CustomModelService,
    private fireflyService: FireflyService,
    private fireflyConfigService: FireflyConfigService,
    private toastService: ToastService,
    private logService: LogService,
    public i18n: I18nService
  ) {}

  ngOnInit(): void {
    this.loadCustomModels();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadCustomModels(): void {
    this.customModelService.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoading => (this.isLoadingCustomModels = isLoading));

    this.customModelService.models$
      .pipe(takeUntil(this.destroy$))
      .subscribe(models => {
        this.customModels = models;

        if (models.length === 0) {
          this.selectedModel = null;
          this.selectedModelId = null;
          return;
        }

        const selectionStillValid =
          this.selectedModelId != null &&
          models.some(m => m.assetId === this.selectedModelId);

        if (!selectionStillValid) {
          this.selectModelById(models[0].assetId);
        }
      });

    this.customModelService.loadCustomModels({
      sortBy: 'modifiedDate',
      limit: 50,
      publishedState: 'published',
    }).subscribe();
  }

  getModelDisplayName(model: CustomModel): string {
    return model.displayName || model.assetName;
  }

  onModelSelect(assetId: string): void {
    this.selectModelById(assetId);
  }

  private selectModelById(assetId: string): void {
    const model = this.customModels.find(m => m.assetId === assetId);
    if (!model) {
      return;
    }

    this.selectedModel = model;
    this.selectedModelId = assetId;
    this.prompt = model.samplePrompt ?? '';

    const newVersion = this.customModelService.getModelVersion(model);
    if (newVersion !== this.selectedModelVersion) {
      this.selectedModelVersion = newVersion;
      this.generationSettings.size = this.usesAspectRatioApi ? '1:1' : '2048x2048';
    }
  }

  triggerStructureFileInput(): void {
    document.getElementById('structureImageInput')?.click();
  }

  onStructureDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isUploadingStructure) {
      this.isDraggingStructure = true;
    }
  }

  onStructureDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingStructure = false;
  }

  onStructureDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingStructure = false;

    if (this.isUploadingStructure) {
      return;
    }

    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.processStructureFile(file);
    }
  }

  onStructureFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    this.processStructureFile(file);
  }

  private processStructureFile(file: File): void {
    if (!file.type.startsWith('image/')) {
      this.toastService.showToast(this.i18n.t('toast.unsupportedFormat'), 'error');
      this.resetStructureFileInput();
      return;
    }

    if (file.size > this.structureImageMaxBytes) {
      this.toastService.showToast(this.i18n.t('cmig.toast.fileTooLarge'), 'error');
      this.resetStructureFileInput();
      return;
    }

    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      this.structureImagePreview = e.target?.result as string;
    };
    reader.readAsDataURL(file);

    this.isUploadingStructure = true;
    this.structureImageId = null;

    this.fireflyService.uploadFile(file).subscribe({
      next: (response) => {
        if (response?.fileId) {
          this.structureImageId = response.fileId;
          this.toastService.showToast(this.i18n.t('cmig.toast.structureUploadSuccess'), 'success');
        } else {
          this.structureImagePreview = null;
          this.toastService.showToast(this.i18n.t('cmig.toast.structureUploadFailed'), 'error');
        }
        this.isUploadingStructure = false;
      },
      error: (error) => {
        console.error('構成画像アップロードエラー:', error);
        this.structureImagePreview = null;
        this.structureImageId = null;
        this.toastService.showToast(this.i18n.t('cmig.toast.structureUploadFailed'), 'error');
        this.isUploadingStructure = false;
      }
    });
  }

  removeStructureImage(): void {
    this.structureImageId = null;
    this.structureImagePreview = null;
    this.resetStructureFileInput();
  }

  private resetStructureFileInput(): void {
    const input = document.getElementById('structureImageInput') as HTMLInputElement | null;
    if (input) {
      input.value = '';
    }
  }

  generateImages(): void {
    if (!this.selectedModel || !this.prompt.trim()) {
      return;
    }

    this.generationPrompt = this.prompt.trim();
    this.isGenerating = true;
    this.generatedImages = [];

    const requestBody = this.usesAspectRatioApi
      ? this.buildIm5RequestBody()
      : this.buildIm4RequestBody();

    this.runGeneration(requestBody, false);
  }

  /**
   * Starts a Firefly generation job. When `append` is false (first call), the
   * results replace `generatedImages`. Once that first job succeeds, a second
   * call is made with a different seed for IM5, and its results are
   * appended to the right of the first image(s).
   */
  private runGeneration(requestBody: object, append: boolean): void {
    if (this.usesAspectRatioApi && append) {
      const previousSeed = (requestBody as { seeds?: number[] }).seeds?.[0];
      const seed = Math.floor(Math.random() * 1000000);
      requestBody = {
        ...requestBody,
        seeds: [seed === previousSeed ? (seed + 1) % 1000000 : seed]
      };
    }

    const apiCall = this.usesAspectRatioApi
      ? this.fireflyService.generateAsyncIm5(requestBody)
      : this.fireflyService.generateAsync(requestBody);

    apiCall.subscribe({
      next: (response) => {
        if (!response.success || !response.jobId) {
          this.toastService.showToast('ジョブの開始に失敗しました', 'error');
          this.isGenerating = false;
          return;
        }
        if (!append) {
          if (this.usesAspectRatioApi) {
            // IM5 currently only supports generating 1 image per request.
            this.sendGenerateContentLog(1);
          }
          else {
            // Fall back for CM4 generation
            this.sendGenerateContentLog(Number(this.generationSettings.numImages));
          }
        }

        this.pollForImages(response.jobId, requestBody, append);
      },
      error: (error) => {
        console.error('画像生成リクエストエラー:', error);
        this.toastService.showToast('画像生成の開始に失敗しました', 'error');
        this.isGenerating = false;
      }
    });
  }

  /**
   * Reusable so other generation calls (e.g. /api/firefly/generate-async) can
   * report the same `generate_content` event once wired up.
   */
  private sendGenerateContentLog(imageCount: number): void {
    this.logService.logGenerateContent({
      tool: 'custom_model_image_generator',
      contentType: 'image',
      model: this.selectedModel?.baseModel?.name,
      modelName: this.selectedModel?.assetName,
      imageCount
    }).subscribe();
  }

  private buildIm4RequestBody(): object {
    const { width, height } = this.parseSize(this.generationSettings.size);
    const options: FireflyGenerationOptions = {
      prompt: this.customModelService.addConceptIdToPrompt(
        this.prompt,
        this.selectedModel!.conceptId ?? ''
      ),
      numVariations: Number(this.generationSettings.numImages) || 1,
      width,
      height,
      promptBiasingLocaleCode: 'ja-JP',
      customModelId: this.selectedModel!.assetId,
      modelVersion: this.selectedModelVersion,
      seed: Math.floor(Math.random() * 1000000),
      ...(this.structureImageId ? { structureImageId: this.structureImageId } : {})
    };
    return this.fireflyConfigService.buildGenerationRequest(options);
  }

  private buildIm5RequestBody(): object {
    const options: FireflyGenerationOptionsIm5 = {
      prompt: this.customModelService.addConceptIdToPrompt(
        this.prompt,
        this.selectedModel!.conceptId ?? ''
      ),
      numVariations: 1, // Currently IM5 only support 1 image generation
      aspectRatio: this.generationSettings.size,
      seed: Math.floor(Math.random() * 1000000),
      promptBiasingLocaleCode: 'ja-JP',
      promptReasoner: 'speed',
      modelId: 'firefly_image',
      customModelId: this.selectedModel!.assetId,
      modelVersion: 'image5_custom',
      resolutionLevel: '2.4MP',
      ...(this.structureImageId? { structureImageId: this.structureImageId } : {})
    };
    return this.fireflyConfigService.buildGenerationRequestIm5(options);
  }

  private pollForImages(jobId: string, requestBody: object, append: boolean): void {
    this.fireflyService.pollJobStatus(jobId).subscribe({
      next: (result) => {
        if (result.status === 'running' || result.status === 'pending') {
          return;
        }

        if (result.status === 'succeeded') {
          if (result.result?.outputs) {
            const newImages = result.result.outputs.map((output: { image: { url: string }; seed?: number }) => ({
              url: output.image.url,
              seed: output.seed
            }));

            this.generatedImages = append ? [...this.generatedImages, ...newImages] : newImages;

            if (this.generatedImages.length > 0) {
              this.hasGeneratedImages = true;
            } else if (!append) {
              this.toastService.showToast('画像生成が完了しましたが、結果が空でした', 'info');
            }
          } else if (!append) {
            this.toastService.showToast('画像生成が完了しましたが、結果が見つかりませんでした', 'info');
          }

          if (!append) {
            // Immediately re-run generation so the
            // second image is displayed to the right of the first one.
            this.runGeneration(requestBody, true);
          } else {
            this.isGenerating = false;
            this.toastService.showToast('画像が生成されました', 'success');
          }
        } else if (result.status === 'failed') {
          this.toastService.showToast(
            `画像生成に失敗しました: ${result.message || '不明なエラー'}`,
            'error'
          );
          this.isGenerating = false;
        }
      },
      error: (error) => {
        console.error(`ジョブ ${jobId} のポーリングエラー:`, error);
        this.toastService.showToast('画像生成のステータス取得に失敗しました', 'error');
        this.isGenerating = false;
      }
    });
  }

  private parseSize(size: string): { width: number; height: number } {
    const [widthStr, heightStr] = size.split('x');
    const width = parseInt(widthStr, 10);
    const height = parseInt(heightStr, 10);

    if (!isNaN(width) && !isNaN(height)) {
      return { width, height };
    }

    return { width: 2048, height: 2048 };
  }

  async downloadImage(image: GeneratedImage, event: Event): Promise<void> {
    event.stopPropagation();

    const filename = `generated-image-${Date.now()}.jpg`;

    try {
      const response = await fetch(image.url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);

      // Log the download action
      this.logService.logGenerateContent({
        tool: 'custom_model_image_generator',
        eventName: 'download_content',
        contentType: 'image',
        model: this.selectedModel?.baseModel?.name,
        modelName: this.selectedModel?.assetName,
        imageCount: 1
      }).subscribe();
    } catch (error) {
      console.error('画像ダウンロードエラー:', error);
      this.toastService.showToast(this.i18n.t('cmig.toast.downloadFailed'), 'error');
    }
  }
}
